"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8001";

interface EmailLoginFormProps {
  onBack?: () => void;
}

export function EmailLoginForm({ onBack }: EmailLoginFormProps) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/email-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setStep("code");
    } catch {
      setError("Failed to send verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/email-verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || "Invalid verification code.");
        return;
      }
      const data = await res.json();
      const userType = data.user_type || "bookkeeper";
      setAuthToken(data.access_token);
      await signIn("credentials", {
        token: data.access_token,
        userType,
        redirect: false,
      });
      // Full page navigation to bypass Router Cache after session change.
      // Go straight to the destination — landing on "/" forces an extra
      // middleware redirect (visible blink + a wasted 401 risk window).
      if (userType === "client") {
        window.location.href = "/client/dashboard";
      } else {
        window.location.href = "/dashboard";
      }
    } catch {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "email") {
    return (
      <form onSubmit={handleSendCode} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {error}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
          ) : (
            <><Mail className="mr-2 h-4 w-4" /> Send Verification Code</>
          )}
        </Button>
        {onBack && (
          <Button type="button" variant="ghost" className="w-full" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        )}
      </form>
    );
  }

  return (
    <form onSubmit={handleVerifyCode} className="space-y-4">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}
      <p className="text-sm text-muted-foreground text-center">
        A verification code was sent to <strong>{email}</strong>
      </p>
      <div className="space-y-2">
        <Label htmlFor="code">Verification Code</Label>
        <Input
          id="code"
          type="text"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          className="text-center text-2xl tracking-widest"
          required
          autoFocus
        />
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
        ) : (
          "Verify & Sign In"
        )}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => { setStep("email"); setCode(""); setError(null); }}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Use different email
      </Button>
    </form>
  );
}
